import { format } from "date-fns";
import { ms } from "date-fns/locale";
import { toast } from "sonner";
import { Globe, Repeat, Trash2, Users, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

export function eventTone(e) {
  const statuses = e.targets.map((t) => t.status);
  if (e.projected) return "border border-dashed border-violet-400/50 text-violet-200";
  if (statuses.includes("failed")) return "bg-red-500/20 text-red-200";
  if (statuses.every((s) => s !== "pending")) return "bg-emerald-500/20 text-emerald-200";
  if (e.targets.some((t) => t.kind === "page")) return "bg-cyan-500/20 text-cyan-200";
  return "bg-primary/25 text-blue-100";
}

const REPEAT_LABEL = { daily: "Setiap hari", weekly: "Setiap minggu" };

function EventCard({ e, onChange, idx }) {
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChange(); } catch (err) { toast.error(errMsg(err)); }
  };
  const del = () => window.confirm("Padam post ini?") && act(() => api.delete(`/posts/${e.post_id}`), "Post dipadam");
  const stop = () => act(() => api.post(`/posts/${e.post_id}/stop-repeat`), "Ulangan dihentikan");
  const pending = e.targets.some((t) => t.status === "pending");

  return (
    <div className="rounded-lg border border-border bg-background/60 p-4" data-testid={`calendar-event-${idx}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={cn("rounded px-2 py-0.5 font-mono text-xs", eventTone(e))}>{format(new Date(e.at), "h:mm a")}</span>
        {e.repeat !== "none" && <span className="flex items-center gap-1 text-[11px] text-violet-300"><Repeat className="h-3 w-3" /> {REPEAT_LABEL[e.repeat]}{e.projected && " (akan datang)"}</span>}
      </div>
      <div className="mt-3 space-y-1">
        {e.targets.map((t, i) => (
          <p key={i} className="flex items-center gap-1.5 text-sm">
            {t.kind === "page" ? <Globe className="h-3.5 w-3.5 text-cyan-300" /> : <Users className="h-3.5 w-3.5 text-primary" />}
            <span className="truncate">{t.name}</span>
          </p>
        ))}
      </div>
      <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-muted-foreground">{e.content}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {e.repeat !== "none" && (
          <Button size="sm" variant="outline" onClick={stop} className="h-8 border-violet-500/40 text-violet-300" data-testid={`calendar-stop-repeat-btn-${idx}`}>
            <XCircle className="mr-1.5 h-3.5 w-3.5" /> Hentikan ulangan
          </Button>
        )}
        {!e.projected && pending && (
          <Button size="sm" variant="ghost" onClick={del} className="h-8 text-red-300" data-testid={`calendar-delete-btn-${idx}`}>
            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Padam
          </Button>
        )}
      </div>
    </div>
  );
}

export default function DayPanel({ day, events, onChange }) {
  return (
    <aside className="panel h-fit p-5 xl:sticky xl:top-24" data-testid="calendar-day-panel">
      <p className="eyebrow">Hari dipilih</p>
      <h2 className="mt-1 text-lg font-bold">{format(day, "EEEE, d MMMM", { locale: ms })}</h2>
      <div className="mt-5 space-y-3">
        {events.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground" data-testid="calendar-day-empty">Tiada post pada hari ini.</p>}
        {events.map((e, i) => <EventCard key={`${e.post_id}-${e.at}`} e={e} idx={i} onChange={onChange} />)}
      </div>
    </aside>
  );
}
